import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService as api } from '../services/api';

interface PatientFeedback {
  id: number;
  tracking_number: string;
  status: 'awaiting_review' | 'analyzing' | 'routed' | 'in_progress' | 'resolved' | 'closed';
  submission_method: string;
  content: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  room_number?: string;
  submitted_at: string;
  severity_level?: 'critical' | 'urgent' | 'routine' | 'informational';
  primary_domain?: string;
  response_due_at?: string;
  routing_reason?: string;
  assignment_id?: number;
  first_response_at?: string;
  resolved_at?: string;
  responses?: StaffResponse[];
  ai_analysis?: AIAnalysis;
  is_visit_related?: boolean;
  visit_type?: 'emergency' | 'outpatient' | 'inpatient' | 'virtual' | '';
  visit_location?: 'er' | 'opd' | 'ward' | 'other' | '';
  urgency_level?: 'standard' | 'urgent' | 'emergency';
  feedback_tags?: string[];
}

interface StaffResponse {
  id: number;
  response_content: string;
  response_type: 'acknowledgment' | 'status_update' | 'resolution' | 'escalation' | 'follow_up';
  sent_at: string;
  staff_member: string;
  is_ai_generated: boolean;
  visible_to_patient: boolean;
}

interface AIAnalysis {
  severity_level: 'critical' | 'urgent' | 'routine' | 'informational';
  primary_domain: string;
  sentiment_score: number;
  frustration_level: string;
  confidence_score: number;
  key_issues: string[];
  recommended_actions: string[];
}

interface StaffFeedbackState {
  assignedFeedback: PatientFeedback[];
  selectedFeedback: PatientFeedback | null;
  isLoading: boolean;
  isLoadingDetail: boolean;
  isSendingResponse: boolean;
  error: string | null;
  filters: {
    status: string;
    severity: string;
    sortBy: 'due_date' | 'submitted_at' | 'severity';
  };
  metrics: {
    total: number;
    critical: number;
    urgent: number;
    routine: number;
    overdue: number;
    avgResponseTime: number;
  };
}

const initialState: StaffFeedbackState = {
  assignedFeedback: [],
  selectedFeedback: null,
  isLoading: false,
  isLoadingDetail: false,
  isSendingResponse: false,
  error: null,
  filters: {
    status: 'all',
    severity: 'all',
    sortBy: 'due_date',
  },
  metrics: {
    total: 0,
    critical: 0,
    urgent: 0,
    routine: 0,
    overdue: 0,
    avgResponseTime: 0,
  },
};

// Async thunks
export const fetchAssignedFeedback = createAsyncThunk(
  'staffFeedback/fetchAssigned',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('staff/assigned/');
      return response;
    } catch (error: any) {
      console.error('[StaffFeedback] Error fetching assigned feedback:', error.response?.status, error.response?.data);
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch assigned feedback');
    }
  }
);

export const fetchFeedbackDetail = createAsyncThunk(
  'staffFeedback/fetchDetail',
  async (feedbackId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`staff/${feedbackId}/`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch feedback details');
    }
  }
);

export const sendStaffResponse = createAsyncThunk(
  'staffFeedback/sendResponse',
  async (
    { feedbackId, responseData }: { 
      feedbackId: number; 
      responseData: {
        response_content: string;
        response_type: string;
        visible_to_patient?: boolean;
        is_internal_note?: boolean;
      }
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`staff/feedback/${feedbackId}/respond/`, responseData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send response');
    }
  }
);

export const acknowledgeFeedback = createAsyncThunk(
  'staffFeedback/acknowledge',
  async (feedbackId: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`staff/feedback/${feedbackId}/acknowledge/`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to acknowledge feedback');
    }
  }
);

export const escalateFeedback = createAsyncThunk(
  'staffFeedback/escalate',
  async (
    { feedbackId, reason, targetUserId }: { 
      feedbackId: number; 
      reason: string;
      targetUserId?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`staff/feedback/${feedbackId}/escalate/`, {
        reason,
        target_user_id: targetUserId,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to escalate feedback');
    }
  }
);

export const reassignFeedback = createAsyncThunk(
  'staffFeedback/reassign',
  async (
    { feedbackId, targetUserId, reason }: { 
      feedbackId: number; 
      targetUserId: number;
      reason?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`staff/feedback/${feedbackId}/reassign/`, {
        target_user_id: targetUserId,
        reason,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to reassign feedback');
    }
  }
);

export const resolveFeedback = createAsyncThunk(
  'staffFeedback/resolve',
  async (
    { feedbackId, resolution }: { 
      feedbackId: number; 
      resolution: {
        resolution_content: string;
        resolution_category: string;
        actions_taken: string[];
        follow_up_required: boolean;
      }
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`staff/feedback/${feedbackId}/resolve/`, resolution);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to resolve feedback');
    }
  }
);

const staffFeedbackSlice = createSlice({
  name: 'staffFeedback',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<StaffFeedbackState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedFeedback: (state) => {
      state.selectedFeedback = null;
    },
    updateFeedbackStatus: (state, action: PayloadAction<{ id: number; status: string }>) => {
      const feedback = state.assignedFeedback.find(f => f.id === action.payload.id);
      if (feedback) {
        feedback.status = action.payload.status as PatientFeedback['status'];
      }
      if (state.selectedFeedback?.id === action.payload.id) {
        state.selectedFeedback.status = action.payload.status as PatientFeedback['status'];
      }
    },
    addNewResponse: (state, action: PayloadAction<{ feedbackId: number; response: StaffResponse }>) => {
      const feedback = state.assignedFeedback.find(f => f.id === action.payload.feedbackId);
      if (feedback && feedback.responses) {
        feedback.responses.push(action.payload.response);
      }
      if (state.selectedFeedback?.id === action.payload.feedbackId && state.selectedFeedback.responses) {
        state.selectedFeedback.responses.push(action.payload.response);
      }
    },
    updateMetrics: (state, action: PayloadAction<Partial<StaffFeedbackState['metrics']>>) => {
      state.metrics = { ...state.metrics, ...action.payload };
    },
    // WebSocket update handlers
    handleFeedbackAssigned: (state, action: PayloadAction<PatientFeedback>) => {
      // Add new feedback to the list if not already present
      if (!state.assignedFeedback.find(f => f.id === action.payload.id)) {
        state.assignedFeedback.unshift(action.payload);
        // Update metrics
        state.metrics.total += 1;
        const severity = action.payload.severity_level;
        if (severity === 'critical') state.metrics.critical += 1;
        else if (severity === 'urgent') state.metrics.urgent += 1;
        else if (severity === 'routine') state.metrics.routine += 1;
      }
    },
    handleFeedbackUpdated: (state, action: PayloadAction<Partial<PatientFeedback> & { id: number }>) => {
      const index = state.assignedFeedback.findIndex(f => f.id === action.payload.id);
      if (index !== -1) {
        state.assignedFeedback[index] = { ...state.assignedFeedback[index], ...action.payload };
      }
      if (state.selectedFeedback?.id === action.payload.id) {
        state.selectedFeedback = { ...state.selectedFeedback, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch assigned feedback
    builder
      .addCase(fetchAssignedFeedback.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssignedFeedback.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        state.assignedFeedback = payload.feedback || [];
        state.metrics = payload.metrics || state.metrics;
      })
      .addCase(fetchAssignedFeedback.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch feedback detail
    builder
      .addCase(fetchFeedbackDetail.pending, (state) => {
        state.isLoadingDetail = true;
        state.error = null;
      })
      .addCase(fetchFeedbackDetail.fulfilled, (state, action) => {
        state.isLoadingDetail = false;
        state.selectedFeedback = action.payload as PatientFeedback;
      })
      .addCase(fetchFeedbackDetail.rejected, (state, action) => {
        state.isLoadingDetail = false;
        state.error = action.payload as string;
      });

    // Send staff response
    builder
      .addCase(sendStaffResponse.pending, (state) => {
        state.isSendingResponse = true;
        state.error = null;
      })
      .addCase(sendStaffResponse.fulfilled, (state, action) => {
        state.isSendingResponse = false;
        // Add response to the feedback
        if (state.selectedFeedback && state.selectedFeedback.id === action.meta.arg.feedbackId) {
          if (!state.selectedFeedback.responses) {
            state.selectedFeedback.responses = [];
          }
          const payload = action.payload as any;
          state.selectedFeedback.responses.push(payload.response);
        }
      })
      .addCase(sendStaffResponse.rejected, (state, action) => {
        state.isSendingResponse = false;
        state.error = action.payload as string;
      });

    // Acknowledge feedback
    builder
      .addCase(acknowledgeFeedback.fulfilled, (state, action) => {
        const feedbackId = action.meta.arg;
        const feedback = state.assignedFeedback.find(f => f.id === feedbackId);
        if (feedback) {
          feedback.status = 'in_progress';
        }
        if (state.selectedFeedback?.id === feedbackId) {
          state.selectedFeedback.status = 'in_progress';
        }
      });

    // Escalate feedback
    builder
      .addCase(escalateFeedback.fulfilled, (state, action) => {
        const feedbackId = action.meta.arg.feedbackId;
        // Remove from assigned list if escalated
        state.assignedFeedback = state.assignedFeedback.filter(f => f.id !== feedbackId);
        if (state.selectedFeedback?.id === feedbackId) {
          state.selectedFeedback = null;
        }
      });

    // Reassign feedback
    builder
      .addCase(reassignFeedback.fulfilled, (state, action) => {
        const feedbackId = action.meta.arg.feedbackId;
        // Remove from assigned list if reassigned
        state.assignedFeedback = state.assignedFeedback.filter(f => f.id !== feedbackId);
        if (state.selectedFeedback?.id === feedbackId) {
          state.selectedFeedback = null;
        }
      });

    // Resolve feedback
    builder
      .addCase(resolveFeedback.fulfilled, (state, action) => {
        const feedbackId = action.meta.arg.feedbackId;
        const feedback = state.assignedFeedback.find(f => f.id === feedbackId);
        if (feedback) {
          feedback.status = 'resolved';
          feedback.resolved_at = new Date().toISOString();
        }
        if (state.selectedFeedback?.id === feedbackId) {
          state.selectedFeedback.status = 'resolved';
          state.selectedFeedback.resolved_at = new Date().toISOString();
        }
      });
  },
});

export const {
  setFilters,
  clearSelectedFeedback,
  updateFeedbackStatus,
  addNewResponse,
  updateMetrics,
  handleFeedbackAssigned,
  handleFeedbackUpdated,
} = staffFeedbackSlice.actions;

export default staffFeedbackSlice.reducer;